import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">该文章可能已被移除或链接无效</p>
            <Link
              to="/"
              className="inline-flex items-center space-x-2 px-5 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-home-line"></i>
              <span>返回首页</span>
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const cat = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <Breadcrumb />
      <main>
        <article className="py-10 md:py-14 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                {cat && (
                  <Link
                    to={`/category/${cat.slug}`}
                    className="text-xs px-3 py-1.5 rounded-full bg-accent-100/80 text-accent-700 hover:bg-accent-200 transition-colors whitespace-nowrap"
                  >
                    {cat.name}
                  </Link>
                )}
                <span className="text-xs text-foreground-400 flex items-center space-x-1">
                  <i className="ri-time-line"></i>
                  <span>{article.readTime}</span>
                </span>
                <span className="text-xs text-foreground-400">{article.date}</span>
                <span className="text-xs text-foreground-400">作者：{article.author}</span>
              </div>
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-4 leading-snug">
                {article.title}
              </h1>
              <p className="text-base text-foreground-500 leading-relaxed">
                {article.excerpt}
              </p>
            </header>

            <div className="mb-10 rounded-lg overflow-hidden">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full aspect-[16/9] object-cover"
                title={article.title}
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed article-content"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100/70 flex items-center justify-center">
                    <i className="ri-user-line text-primary-500"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">{article.author}</p>
                    <p className="text-xs text-foreground-400">哔咔漫画教程作者</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <button className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center hover:bg-primary-100/70 transition-colors cursor-pointer" aria-label="分享到微信">
                    <i className="ri-wechat-line text-foreground-600"></i>
                  </button>
                  <button className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center hover:bg-primary-100/70 transition-colors cursor-pointer" aria-label="分享到微博">
                    <i className="ri-weibo-line text-foreground-600"></i>
                  </button>
                  <button className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center hover:bg-primary-100/70 transition-colors cursor-pointer" aria-label="复制链接">
                    <i className="ri-link text-foreground-600"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-12 md:py-16 bg-background-100">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-8">
                相关文章推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.slug}
                    to={`/article/${related.slug}`}
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200/70 transition-all duration-300 cursor-pointer"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={related.coverImage}
                        alt={related.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        title={related.title}
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-heading text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug">
                        {related.title}
                      </h3>
                      <p className="text-xs text-foreground-400 mt-2">{related.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            image: article.coverImage,
            datePublished: article.date,
            author: {
              '@type': 'Person',
              name: article.author,
            },
            publisher: {
              '@type': 'Organization',
              name: '哔咔漫画官方指南',
            },
          }),
        }}
      />
    </>
  );
}